'use client';

import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  type KeyboardEvent as ReactKeyboardEvent
} from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  ArrowDownUp,
  ChevronDown,
  ChevronUp,
  Download,
  FileText,
  Loader2,
  Maximize,
  Maximize2,
  Minus,
  Plus,
  Printer,
  RotateCcw,
  RotateCw,
  Search,
  X
} from 'lucide-react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { PrintPortal } from '@/components/print-portal';
import { useAuthenticatedBlobUrl } from '@/hooks/use-authenticated-blob-url';
import {
  decodeQrLocationFromBlob,
  type QrLocationInfo,
  type QrBboxData
} from '@/lib/qr/decode';

// Local chunk type — kept identical to the OCR feature's shape so this viewer
// can later be reused for OCR'd batches without cross-feature imports.
interface DocumentChunk {
  id: string | number;
  chunk_id: string;
  bbox: { left: number; top: number; width: number; height: number };
  content?: string | null;
}

export type { QrOverlayStatus } from '@/lib/qr/qr-overlay-styles';
import type { QrOverlayStatus } from '@/lib/qr/qr-overlay-styles';
import { QrOverlay } from '@/components/qr-overlay';

// Module-scoped cache of QR decode results keyed by the resolved blob URL.
// Decoding a 4–8 MB scan is expensive (image bitmap + jsQR sweep), so we only
// want to pay it once per unique image — and only for pages the parent has
// flagged with a `qrStatus`. `null` means "we tried and there's no QR" so we
// don't loop on a result we already know isn't there.
const qrLocationCache = new Map<string, QrLocationInfo | null>();
const qrLocationInflight = new Map<string, Promise<QrLocationInfo | null>>();

function useQrLocation(
  blobUrl: string | null,
  enabled: boolean
): QrLocationInfo | null {
  const [value, setValue] = useState<QrLocationInfo | null>(() =>
    blobUrl && qrLocationCache.has(blobUrl)
      ? (qrLocationCache.get(blobUrl) ?? null)
      : null
  );

  useEffect(() => {
    if (!enabled || !blobUrl) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setValue(null);
      return;
    }
    if (qrLocationCache.has(blobUrl)) {
      setValue(qrLocationCache.get(blobUrl) ?? null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        let pending = qrLocationInflight.get(blobUrl);
        if (!pending) {
          pending = fetch(blobUrl)
            .then((r) => r.blob())
            .then((b) => decodeQrLocationFromBlob(b));
          qrLocationInflight.set(blobUrl, pending);
        }
        const result = await pending;
        qrLocationCache.set(blobUrl, result);
        qrLocationInflight.delete(blobUrl);
        if (!cancelled) setValue(result);
      } catch {
        qrLocationCache.set(blobUrl, null);
        qrLocationInflight.delete(blobUrl);
        if (!cancelled) setValue(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [blobUrl, enabled]);

  return value;
}

// ── Zoom levels ────────────────────────────────────────────────────────────
const ZOOM_LEVELS = [0.25, 0.33, 0.5, 0.67, 0.75, 1, 1.25, 1.5, 2, 2.5, 3];
const DEFAULT_ZOOM_INDEX = 5; // 100% — only reached when user manually steps from a fit mode
const MIN_ZOOM = ZOOM_LEVELS[0];
const MAX_ZOOM = ZOOM_LEVELS[ZOOM_LEVELS.length - 1];

type FitMode = 'none' | 'width' | 'page';

// ── Single page image with chunk overlays ──────────────────────────────────
function PageImage({
  imageUrl,
  authEndpoint,
  chunks,
  highlightedChunkIds,
  activeChunkIds,
  searchMatchChunkIds,
  activeSearchChunkId,
  isProcessing,
  qrStatus,
  qrBbox,
  qrLabel,
  onQrLocationDecoded,
  onChunkClick,
  onChunkHover,
  onImageLoad,
  rotation,
  innerWidth,
  innerHeight,
  outerWidth,
  outerHeight
}: {
  imageUrl: string | null;
  authEndpoint?: string | null;
  chunks: DocumentChunk[];
  highlightedChunkIds: string[];
  activeChunkIds: string[];
  searchMatchChunkIds: string[];
  activeSearchChunkId: string | null;
  isProcessing: boolean;
  qrStatus?: QrOverlayStatus | null;
  qrBbox?: QrBboxData | null;
  qrLabel?: string | null;
  onQrLocationDecoded?: (corners: QrBboxData['corners']) => void;
  onChunkClick?: (chunkId: string) => void;
  onChunkHover?: (chunkId: string | null) => void;
  onImageLoad?: (naturalWidth: number, naturalHeight: number) => void;
  rotation: number;
  /** Un-rotated image size (natural × zoom). */
  innerWidth: number;
  innerHeight: number;
  /** Rotation-aware outer slot — equals inner when rotation is 0°/180°,
   *  swapped when 90°/270°. The wrapper reserves this space so a rotated
   *  page doesn't bleed into its neighbours. */
  outerWidth: number;
  outerHeight: number;
}) {
  const t = useTranslations('photoScanning');
  const imgRef = useRef<HTMLImageElement>(null);
  const [loaded, setLoaded] = useState(false);
  // When `authEndpoint` is set, the page image is served by a permissioned API
  // route that needs a Bearer token — fetch it as a blob via the shared LRU
  // cache so the URL can be embedded as a regular <img src>. Otherwise fall
  // back to the direct (signed) URL.
  const blobUrl = useAuthenticatedBlobUrl(authEndpoint ?? null);
  const resolvedUrl = authEndpoint ? blobUrl : imageUrl;
  // Server-supplied bbox wins — it was captured during upload from the
  // operator's original (pre-storage) bytes and is deterministic. The
  // client-side decoder only runs for legacy rows uploaded before this was
  // persisted.
  const persistedQrInfo = useMemo<QrLocationInfo | null>(() => {
    if (!qrBbox || qrBbox.corners.length !== 4) return null;
    const xs = qrBbox.corners.map((c) => c.x);
    const ys = qrBbox.corners.map((c) => c.y);
    const left = Math.min(...xs);
    const right = Math.max(...xs);
    const top = Math.min(...ys);
    const bottom = Math.max(...ys);
    return {
      data: '',
      bbox: {
        left,
        top,
        width: Math.max(0, right - left),
        height: Math.max(0, bottom - top)
      },
      corners: qrBbox.corners
    };
  }, [qrBbox]);
  // Only fire the client-side decoder if we have no persisted bbox — fetching
  // the blob before it's in the browser cache would just race the <img> tag.
  const decodedQrInfo = useQrLocation(
    !persistedQrInfo && loaded ? resolvedUrl : null,
    qrStatus != null && !persistedQrInfo && loaded
  );

  // Backfill: when the client-side fallback succeeds on a legacy row, fire the
  // callback exactly once so the parent can persist the corners server-side.
  // After that, the next page load reads them straight from the row and the
  // overlay renders instantly. Guarded by a ref so a re-render with the same
  // decode result doesn't re-fire.
  const backfilledRef = useRef<string | null>(null);
  useEffect(() => {
    if (persistedQrInfo) return;
    if (!decodedQrInfo) return;
    if (!onQrLocationDecoded) return;
    const fingerprint = decodedQrInfo.corners
      .map((c) => `${c.x.toFixed(4)},${c.y.toFixed(4)}`)
      .join('|');
    if (backfilledRef.current === fingerprint) return;
    backfilledRef.current = fingerprint;
    onQrLocationDecoded(decodedQrInfo.corners);
  }, [persistedQrInfo, decodedQrInfo, onQrLocationDecoded]);
  // Render-phase reset: when the URL changes (e.g. after rotation) we want the
  // spinner back until the new image fires `onLoad`. Doing this in `useEffect`
  // would trigger a cascading render — React's docs recommend this pattern.
  const [trackedUrl, setTrackedUrl] = useState<string | null>(resolvedUrl);
  if (trackedUrl !== resolvedUrl) {
    setTrackedUrl(resolvedUrl);
    setLoaded(false);
  }

  if (!resolvedUrl) {
    return (
      <div
        className='bg-muted/20 mx-auto flex items-center justify-center rounded-lg'
        style={{ width: outerWidth, height: outerHeight }}
      >
        <FileText className='text-muted-foreground/20 h-12 w-12' />
      </div>
    );
  }

  return (
    <div
      className='relative mx-auto select-none'
      style={{ width: outerWidth, height: outerHeight }}
    >
      {/* Inner container is sized for the un-rotated image and rotates around
          its own center. The outer wrapper reserves the rotation-aware
          bounding box so neighbouring pages aren't pushed around. */}
      <div
        className='absolute top-1/2 left-1/2 transition-transform duration-200'
        style={{
          width: innerWidth,
          height: innerHeight,
          transform: `translate(-50%, -50%) rotate(${rotation}deg)`
        }}
      >
        {/* eslint-disable-next-line @next/next/no-img-element -- authenticated document scan; next/image proxy cannot forward Bearer token, and review requires full-resolution */}
        <img
          ref={imgRef}
          src={resolvedUrl}
          alt='Page'
          loading='lazy'
          decoding='async'
          className={cn(
            'block h-full w-full rounded shadow-md',
            !loaded && 'bg-muted/20'
          )}
          onLoad={(e) => {
            setLoaded(true);
            onImageLoad?.(
              e.currentTarget.naturalWidth,
              e.currentTarget.naturalHeight
            );
          }}
          draggable={false}
        />

        {/* Chunk bounding-box overlays — percentage-based so they track the
            un-rotated image, then ride along with the parent's rotation. */}
        <div className='absolute inset-0'>
          {chunks.map((chunk) => {
            const isActive = activeChunkIds.includes(chunk.chunk_id);
            const isHighlighted = highlightedChunkIds.includes(chunk.chunk_id);
            const isSearchActive = activeSearchChunkId === chunk.chunk_id;
            const isSearchMatch = searchMatchChunkIds.includes(chunk.chunk_id);

            return (
              <div
                key={chunk.id}
                className={cn(
                  'absolute cursor-pointer rounded-[2px] transition-all duration-200',
                  // Active / search-active states drop the inner border and
                  // most of the fill so the text underneath stays readable —
                  // emphasis comes from the OUTER glow ring, not from
                  // overlaying color on the glyphs. Critical for multi-region
                  // `boxes_2d` results that paint several boxes at once.
                  isSearchActive
                    ? 'bg-orange-500/10 shadow-[0_0_0_3px_rgba(249,115,22,0.45)]'
                    : isSearchMatch
                      ? 'border-[1.5px] border-orange-400/60 bg-orange-400/10'
                      : isActive
                        ? 'bg-red-500/10 shadow-[0_0_0_3px_rgba(239,68,68,0.50)]'
                        : isHighlighted
                          ? 'bg-blue-500/10 shadow-[0_0_0_3px_rgba(59,130,246,0.40)]'
                          : 'border-primary/20 bg-primary/5 hover:border-primary/40 hover:bg-primary/10 border-[1.5px]'
                )}
                style={{
                  left: `${chunk.bbox.left * 100}%`,
                  top: `${chunk.bbox.top * 100}%`,
                  width: `${chunk.bbox.width * 100}%`,
                  height: `${chunk.bbox.height * 100}%`
                }}
                onClick={() => onChunkClick?.(chunk.chunk_id)}
                onMouseEnter={() => onChunkHover?.(chunk.chunk_id)}
                onMouseLeave={() => onChunkHover?.(null)}
              />
            );
          })}

          {/* QR status overlay — shared component renders a corner chip plus
              (when decode succeeds) a precise polygon over the printed QR's
              corners. Both sit inside the rotated wrapper so they ride any
              optimistic rotation. */}
          <QrOverlay
            status={qrStatus ?? null}
            qrBbox={qrBbox ?? null}
            liveCorners={decodedQrInfo?.corners ?? null}
            label={qrLabel ?? null}
          />

          {/* Scanning animation during processing */}
          <AnimatePresence>
            {isProcessing && (
              <motion.div
                className='pointer-events-none absolute inset-0 overflow-hidden'
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6 }}
              >
                <motion.div
                  className='absolute inset-x-0'
                  animate={{ top: ['-2%', '102%'] }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: 'linear',
                    repeatDelay: 0.3
                  }}
                >
                  <div className='via-primary h-px bg-gradient-to-r from-transparent to-transparent' />
                  <div className='via-primary/20 h-4 -translate-y-2 bg-gradient-to-r from-transparent to-transparent blur-lg' />
                </motion.div>

                {(
                  [
                    'start-0 top-0 border-s-2 border-t-2',
                    'end-0 top-0 border-e-2 border-t-2',
                    'start-0 bottom-0 border-s-2 border-b-2',
                    'end-0 bottom-0 border-e-2 border-b-2'
                  ] as const
                ).map((classes, i) => (
                  <motion.div
                    key={i}
                    className={cn(
                      'border-primary/50 absolute h-5 w-5',
                      classes
                    )}
                    animate={{ opacity: [0.3, 0.8, 0.3] }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      delay: i * 0.15,
                      ease: 'easeInOut'
                    }}
                  />
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {!loaded && (
        <div className='pointer-events-none absolute inset-0 flex items-center justify-center'>
          <Loader2 className='text-muted-foreground h-6 w-6 animate-spin' />
        </div>
      )}
    </div>
  );
}

// ── Types ──────────────────────────────────────────────────────────────────
export interface ViewerPage {
  imageUrl: string | null;
  /**
   * Optional API endpoint that requires a Bearer token (e.g. the
   * photo-scanning page-image route). When set, the viewer fetches it as an
   * authenticated blob and uses that instead of `imageUrl`.
   */
  authEndpoint?: string | null;
  label: string;
  chunks: DocumentChunk[];
  isProcessing: boolean;
  /**
   * When set, the viewer draws a status-coloured polygon over the printed
   * QR's location. The polygon is sourced from `qrBbox` when present
   * (captured at upload time, persisted server-side, deterministic) or, as
   * a backward-compatibility fallback for rows uploaded before this was
   * captured, by re-decoding the image client-side.
   */
  qrStatus?: QrOverlayStatus | null;
  /** Persisted QR location in 0..1 image fractions. When set, the viewer
   *  skips the client-side decoder and renders directly from these corners. */
  qrBbox?: QrBboxData | null;
  /** Optional human-readable label appended to the overlay badge — e.g. the page's barcode. */
  qrLabel?: string | null;
  /** Fired once per page after the client-side fallback decoder successfully
   *  locates the QR for a legacy row (i.e. one without a persisted `qrBbox`).
   *  The parent can post the corners back to the server to backfill, so the
   *  next view renders instantly from the stored value. */
  onQrLocationDecoded?: (corners: QrBboxData['corners']) => void;
}

interface SearchMatch {
  pageIndex: number;
  chunkId: string;
}

interface DocumentPagesViewerProps {
  pages: ViewerPage[];
  pdfUrl?: string | null;
  highlightedChunkIds: string[];
  activeChunkIds: string[];
  activePage?: number;
  onChunkClick?: (chunkId: string) => void;
  onChunkHover?: (chunkId: string | null) => void;
  onPageChange?: (pageIndex: number) => void;
  /**
   * Persist rotation on the server. When provided, the rotate buttons call
   * this with the page index and delta in degrees (90 or -90) instead of
   * applying a client-side CSS transform — the parent should refetch the
   * affected page image after the mutation resolves.
   */
  onRotatePage?: (pageIndex: number, delta: number) => void;
  /** Renders a spinner on the rotate buttons and disables them. */
  isRotating?: boolean;
  /**
   * When provided, the toolbar shows a Rearrange button. The parent owns the
   * dialog and the persistence — the viewer only emits the click.
   */
  onRearrangePages?: () => void;
}

// ── Search bar ────────────────────────────────────────────────────────────
function SearchBar({
  matches,
  currentIndex,
  onQueryChange,
  onNext,
  onPrev,
  onClose
}: {
  matches: SearchMatch[];
  currentIndex: number;
  onQueryChange: (q: string) => void;
  onNext: () => void;
  onPrev: () => void;
  onClose: () => void;
}) {
  const t = useTranslations('ocr');
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleChange = (value: string) => {
    setQuery(value);
    onQueryChange(value);
  };

  const handleKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onNext();
    } else if (e.key === 'Enter' && e.shiftKey) {
      e.preventDefault();
      onPrev();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.15 }}
      className='absolute inset-x-0 bottom-14 z-10 flex justify-center px-4'
    >
      <div className='bg-background/95 flex items-center gap-1.5 rounded-lg border px-2 py-1.5 shadow-md backdrop-blur-sm'>
        <Search className='text-muted-foreground h-3.5 w-3.5 shrink-0' />
        <Input
          ref={inputRef}
          value={query}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('searchPlaceholder')}
          className='h-7 w-48 border-0 bg-transparent px-1 text-xs shadow-none focus-visible:ring-0'
        />

        {query.trim() && (
          <span className='text-muted-foreground min-w-[3.5rem] text-center text-[10px] tabular-nums'>
            {matches.length > 0
              ? t('matchCount', {
                  current: currentIndex + 1,
                  total: matches.length
                })
              : t('noMatches')}
          </span>
        )}

        <Separator orientation='vertical' className='mx-0.5 h-4' />

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='ghost'
              size='icon'
              className='h-6 w-6'
              onClick={onPrev}
              disabled={matches.length === 0}
            >
              <ChevronUp className='h-3.5 w-3.5' />
            </Button>
          </TooltipTrigger>
          <TooltipContent side='top'>
            <p>{t('prevMatch')}</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='ghost'
              size='icon'
              className='h-6 w-6'
              onClick={onNext}
              disabled={matches.length === 0}
            >
              <ChevronDown className='h-3.5 w-3.5' />
            </Button>
          </TooltipTrigger>
          <TooltipContent side='top'>
            <p>{t('nextMatch')}</p>
          </TooltipContent>
        </Tooltip>

        <Separator orientation='vertical' className='mx-0.5 h-4' />

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='ghost'
              size='icon'
              className='h-6 w-6'
              onClick={onClose}
            >
              <X className='h-3.5 w-3.5' />
            </Button>
          </TooltipTrigger>
          <TooltipContent side='top'>
            <p>{t('closeSearch')}</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </motion.div>
  );
}

// ── Toolbar ────────────────────────────────────────────────────────────────
function ViewerToolbar({
  currentPage,
  totalPages,
  effectiveZoom,
  fitMode,
  canDownload,
  canRotate,
  isRotating,
  onPrevPage,
  onNextPage,
  onGoToPage,
  onZoomIn,
  onZoomOut,
  onFitToWidth,
  onFitToPage,
  onRotateLeft,
  onRotateRight,
  onToggleSearch,
  onPrint,
  onDownload,
  onRearrangePages
}: {
  currentPage: number;
  totalPages: number;
  effectiveZoom: number;
  fitMode: FitMode;
  canDownload: boolean;
  canRotate: boolean;
  isRotating: boolean;
  onPrevPage: () => void;
  onNextPage: () => void;
  onGoToPage: (page: number) => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFitToWidth: () => void;
  onFitToPage: () => void;
  onRotateLeft: () => void;
  onRotateRight: () => void;
  onToggleSearch: () => void;
  onPrint: () => void;
  onDownload: () => void;
  onRearrangePages?: () => void;
}) {
  const t = useTranslations('ocr');
  const zoomPercent = `${Math.round(effectiveZoom * 100)}%`;
  const canZoomIn = effectiveZoom < MAX_ZOOM - 0.001;
  const canZoomOut = effectiveZoom > MIN_ZOOM + 0.001;

  const [pageInput, setPageInput] = useState(String(currentPage + 1));

  // Sync input when page changes externally
  useEffect(() => {
    setPageInput(String(currentPage + 1));
  }, [currentPage]);

  const commitPageInput = () => {
    const num = parseInt(pageInput, 10);
    if (!isNaN(num) && num >= 1 && num <= totalPages) {
      onGoToPage(num - 1);
    } else {
      setPageInput(String(currentPage + 1));
    }
  };

  const handlePageInputKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitPageInput();
      (e.target as HTMLInputElement).blur();
    }
  };

  return (
    <div className='absolute inset-x-0 bottom-0 z-10 flex justify-center px-4 pb-3'>
      <div className='bg-background/90 flex items-center gap-1 rounded-lg border px-2 py-1 shadow-md backdrop-blur-sm'>
        {/* ── Zoom controls ── */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='ghost'
              size='icon'
              className='h-7 w-7'
              onClick={onZoomOut}
              disabled={!canZoomOut}
            >
              <Minus className='h-3.5 w-3.5' />
            </Button>
          </TooltipTrigger>
          <TooltipContent side='top'>
            <p>{t('zoomOut')}</p>
          </TooltipContent>
        </Tooltip>

        <span
          className={cn(
            'min-w-[3rem] text-center text-xs font-medium tabular-nums',
            fitMode !== 'none' && 'text-primary'
          )}
          title={
            fitMode === 'page'
              ? t('fitToPage')
              : fitMode === 'width'
                ? t('fitToWidth')
                : undefined
          }
        >
          {zoomPercent}
        </span>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='ghost'
              size='icon'
              className='h-7 w-7'
              onClick={onZoomIn}
              disabled={!canZoomIn}
            >
              <Plus className='h-3.5 w-3.5' />
            </Button>
          </TooltipTrigger>
          <TooltipContent side='top'>
            <p>{t('zoomIn')}</p>
          </TooltipContent>
        </Tooltip>

        {/* ── Fit to page ── */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={fitMode === 'page' ? 'secondary' : 'ghost'}
              size='icon'
              className='h-7 w-7'
              onClick={onFitToPage}
            >
              <Maximize2 className='h-3.5 w-3.5' />
            </Button>
          </TooltipTrigger>
          <TooltipContent side='top'>
            <p>{t('fitToPage')}</p>
          </TooltipContent>
        </Tooltip>

        {/* ── Fit to width ── */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={fitMode === 'width' ? 'secondary' : 'ghost'}
              size='icon'
              className='h-7 w-7'
              onClick={onFitToWidth}
            >
              <Maximize className='h-3.5 w-3.5' />
            </Button>
          </TooltipTrigger>
          <TooltipContent side='top'>
            <p>{t('fitToWidth')}</p>
          </TooltipContent>
        </Tooltip>

        <Separator orientation='vertical' className='mx-1 h-5' />

        {/* ── Page navigation ── */}
        {totalPages > 1 && (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <Input
                  value={pageInput}
                  onChange={(e) => setPageInput(e.target.value)}
                  onBlur={commitPageInput}
                  onKeyDown={handlePageInputKeyDown}
                  className='h-7 w-10 border-0 bg-transparent px-0 text-center text-xs font-medium tabular-nums shadow-none focus-visible:ring-1'
                  title={t('goToPage')}
                />
              </TooltipTrigger>
              <TooltipContent side='top'>
                <p>{t('goToPage')}</p>
              </TooltipContent>
            </Tooltip>

            <span className='text-muted-foreground text-xs tabular-nums'>
              / {totalPages}
            </span>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant='ghost'
                  size='icon'
                  className='h-7 w-7'
                  onClick={onPrevPage}
                  disabled={currentPage <= 0}
                >
                  <ChevronUp className='h-4 w-4' />
                </Button>
              </TooltipTrigger>
              <TooltipContent side='top'>
                <p>{t('prevPage')}</p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant='ghost'
                  size='icon'
                  className='h-7 w-7'
                  onClick={onNextPage}
                  disabled={currentPage >= totalPages - 1}
                >
                  <ChevronDown className='h-4 w-4' />
                </Button>
              </TooltipTrigger>
              <TooltipContent side='top'>
                <p>{t('nextPage')}</p>
              </TooltipContent>
            </Tooltip>

            <Separator orientation='vertical' className='mx-1 h-5' />
          </>
        )}

        {/* ── Rotate (left + right) — only when rotation is wired (read-write).
            Hidden when rotation isn't wired (e.g. a batch handed off to QA),
            since the buttons would otherwise be no-ops. ── */}
        {canRotate && (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant='ghost'
                  size='icon'
                  className='h-7 w-7'
                  onClick={onRotateLeft}
                  disabled={isRotating}
                >
                  {isRotating ? (
                    <Loader2 className='h-3.5 w-3.5 animate-spin' />
                  ) : (
                    <RotateCcw className='h-3.5 w-3.5' />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side='top'>
                <p>{t('rotate')}</p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant='ghost'
                  size='icon'
                  className='h-7 w-7'
                  onClick={onRotateRight}
                  disabled={isRotating}
                >
                  {isRotating ? (
                    <Loader2 className='h-3.5 w-3.5 animate-spin' />
                  ) : (
                    <RotateCw className='h-3.5 w-3.5' />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side='top'>
                <p>{t('rotate')}</p>
              </TooltipContent>
            </Tooltip>
          </>
        )}

        {onRearrangePages && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant='ghost'
                size='icon'
                className='h-7 w-7'
                onClick={onRearrangePages}
                disabled={totalPages < 2}
              >
                <ArrowDownUp className='h-3.5 w-3.5' />
              </Button>
            </TooltipTrigger>
            <TooltipContent side='top'>
              <p>{t('rearrangePages')}</p>
            </TooltipContent>
          </Tooltip>
        )}

        <Separator orientation='vertical' className='mx-1 h-5' />

        {/* ── Actions ── */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='ghost'
              size='icon'
              className='h-7 w-7'
              onClick={onToggleSearch}
            >
              <Search className='h-3.5 w-3.5' />
            </Button>
          </TooltipTrigger>
          <TooltipContent side='top'>
            <p>{t('findInDocument')}</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='ghost'
              size='icon'
              className='h-7 w-7'
              onClick={onPrint}
            >
              <Printer className='h-3.5 w-3.5' />
            </Button>
          </TooltipTrigger>
          <TooltipContent side='top'>
            <p>{t('print')}</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='ghost'
              size='icon'
              className='h-7 w-7'
              onClick={onDownload}
              disabled={!canDownload}
            >
              <Download className='h-3.5 w-3.5' />
            </Button>
          </TooltipTrigger>
          <TooltipContent side='top'>
            <p>{t('download')}</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}

// ── Print view ────────────────────────────────────────────────────────────
function PrintPage({ page, isLast }: { page: ViewerPage; isLast: boolean }) {
  const blobUrl = useAuthenticatedBlobUrl(page.authEndpoint ?? null);
  const src = page.authEndpoint ? blobUrl : page.imageUrl;

  return (
    <div
      style={{
        width: '194mm',
        height: '281mm',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        fontFamily: 'system-ui, sans-serif',
        background: '#fff',
        pageBreakAfter: isLast ? 'auto' : 'always',
        breakAfter: isLast ? 'auto' : 'page',
        pageBreakInside: 'avoid',
        breakInside: 'avoid'
      }}
    >
      <p
        style={{
          fontSize: '10px',
          color: '#666',
          margin: 0,
          flex: '0 0 auto'
        }}
      >
        {page.label}
      </p>
      {src && (
        /* eslint-disable-next-line @next/next/no-img-element -- authenticated document scan; next/image proxy cannot forward Bearer token, and review requires full-resolution */
        <img
          src={src}
          alt={page.label}
          style={{
            flex: '1 1 auto',
            minHeight: 0,
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain'
          }}
        />
      )}
    </div>
  );
}

function PrintView({ pages }: { pages: ViewerPage[] }) {
  return (
    <PrintPortal>
      <div>
        {pages.map((page, i) => (
          <PrintPage key={i} page={page} isLast={i === pages.length - 1} />
        ))}
      </div>
    </PrintPortal>
  );
}

// ── Main viewer ────────────────────────────────────────────────────────────
export function DocumentPagesViewer({
  pages,
  pdfUrl,
  highlightedChunkIds,
  activeChunkIds,
  activePage,
  onChunkClick,
  onChunkHover,
  onPageChange,
  onRotatePage,
  isRotating = false,
  onRearrangePages
}: DocumentPagesViewerProps) {
  const t = useTranslations('ocr');
  const scrollRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const [visiblePage, setVisiblePage] = useState(0);
  const isAutoScrolling = useRef(false);

  // Toolbar state
  const [zoomIndex, setZoomIndex] = useState(DEFAULT_ZOOM_INDEX);
  // Per-page rotation. Always applied optimistically — when the user clicks
  // rotate the CSS transform updates instantly so they get immediate feedback.
  // If `onRotatePage` is wired, the parent persists rotation on the server
  // and the affected image refetches; we then clear that page's optimistic
  // entry on `onBlobReady` so the rotated bytes aren't double-rotated.
  const [pageRotations, setPageRotations] = useState<Map<number, number>>(
    new Map()
  );
  // Default to fit-to-width so each page fills the viewer's full width (and
  // grows tall enough to fill the height, scrolling when needed) instead of
  // shrinking to fit both dimensions and leaving wide empty margins. The
  // operator can still switch to fit-to-page from the toolbar.
  const [fitMode, setFitMode] = useState<FitMode>('width');
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  // Natural pixel dimensions per loaded page. Kept in state (not a ref) so
  // page wrappers re-render with their `displayWidth = naturalWidth * zoom`
  // once an image reports its dimensions.
  const [imageNaturalDims, setImageNaturalDims] = useState<
    Map<number, { width: number; height: number }>
  >(new Map());

  const firstPageRotation = pageRotations.get(0) ?? 0;

  // Search state
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);

  // Print state
  const [printMode, setPrintMode] = useState(false);

  // ── Compute effective zoom ──
  const effectiveZoom = useMemo(() => {
    if (fitMode === 'none' || containerSize.width === 0)
      return ZOOM_LEVELS[zoomIndex];

    const firstDims = imageNaturalDims.get(0);
    if (!firstDims) return ZOOM_LEVELS[zoomIndex];

    const isRotatedSideways = firstPageRotation % 180 !== 0;
    const refW = isRotatedSideways ? firstDims.height : firstDims.width;
    const refH = isRotatedSideways ? firstDims.width : firstDims.height;

    // Padding budget matches the scroll wrapper's `p-4` (16px each side) plus
    // the `pb-14` toolbar gutter (~56px) and the page label row above the image.
    const padX = 32;
    const padY = 32 + 56 + 28;

    const scaleW = Math.max(containerSize.width - padX, 0) / refW;
    if (fitMode === 'width') return scaleW;

    const scaleH = Math.max(containerSize.height - padY, 0) / refH;
    return Math.min(scaleW, scaleH);
  }, [fitMode, containerSize, zoomIndex, firstPageRotation, imageNaturalDims]);

  // ── Track container size for fit modes ──
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height
        });
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // ── Search logic ──
  const searchMatches = useMemo<SearchMatch[]>(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    const matches: SearchMatch[] = [];
    pages.forEach((page, pageIndex) => {
      page.chunks.forEach((chunk) => {
        if (chunk.content?.toLowerCase().includes(q)) {
          matches.push({ pageIndex, chunkId: chunk.chunk_id });
        }
      });
    });
    return matches;
  }, [searchQuery, pages]);

  // Reset current match when matches change
  useEffect(() => {
    setCurrentMatchIndex(0);
  }, [searchMatches.length]);

  const searchMatchChunkIds = useMemo(
    () => searchMatches.map((m) => m.chunkId),
    [searchMatches]
  );

  const activeSearchChunkId =
    searchMatches.length > 0 ? searchMatches[currentMatchIndex]?.chunkId : null;

  // Navigate to the current search match page
  useEffect(() => {
    if (searchMatches.length === 0) return;
    const match = searchMatches[currentMatchIndex];
    if (!match) return;
    goToPage(match.pageIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMatchIndex, searchMatches]);

  const handleSearchNext = useCallback(() => {
    if (searchMatches.length === 0) return;
    setCurrentMatchIndex((i) => (i + 1) % searchMatches.length);
  }, [searchMatches.length]);

  const handleSearchPrev = useCallback(() => {
    if (searchMatches.length === 0) return;
    setCurrentMatchIndex(
      (i) => (i - 1 + searchMatches.length) % searchMatches.length
    );
  }, [searchMatches.length]);

  const handleSearchClose = useCallback(() => {
    setSearchOpen(false);
    setSearchQuery('');
    setCurrentMatchIndex(0);
  }, []);

  // ── Auto-scroll to activePage when it changes externally ──
  useEffect(() => {
    if (activePage == null || activePage === visiblePage) return;
    const el = pageRefs.current.get(activePage);
    if (!el) return;

    isAutoScrolling.current = true;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const timer = setTimeout(() => {
      isAutoScrolling.current = false;
    }, 600);
    return () => clearTimeout(timer);
  }, [activePage]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Track visible page from user scroll ──
  const handleScroll = useCallback(() => {
    if (isAutoScrolling.current) return;
    const container = scrollRef.current;
    if (!container) return;

    const scrollTop = container.scrollTop;
    let closestIdx = 0;
    let closestDist = Infinity;

    for (const [idx, el] of pageRefs.current) {
      const dist = Math.abs(el.offsetTop - scrollTop - 16);
      if (dist < closestDist) {
        closestDist = dist;
        closestIdx = idx;
      }
    }

    if (closestIdx !== visiblePage) {
      setVisiblePage(closestIdx);
      onPageChange?.(closestIdx);
    }
  }, [visiblePage, onPageChange]);

  // ── Page navigation ──
  const goToPage = useCallback(
    (index: number) => {
      const clamped = Math.max(0, Math.min(index, pages.length - 1));
      const el = pageRefs.current.get(clamped);
      if (!el) return;

      isAutoScrolling.current = true;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setVisiblePage(clamped);
      onPageChange?.(clamped);
      setTimeout(() => {
        isAutoScrolling.current = false;
      }, 600);
    },
    [pages.length, onPageChange]
  );

  // ── Zoom handlers (snap to the nearest standard level, exit fit mode) ──
  const handleZoomIn = useCallback(() => {
    const next = ZOOM_LEVELS.find((z) => z > effectiveZoom + 0.001) ?? MAX_ZOOM;
    setZoomIndex(ZOOM_LEVELS.indexOf(next));
    setFitMode('none');
  }, [effectiveZoom]);

  const handleZoomOut = useCallback(() => {
    const next =
      [...ZOOM_LEVELS].reverse().find((z) => z < effectiveZoom - 0.001) ??
      MIN_ZOOM;
    setZoomIndex(ZOOM_LEVELS.indexOf(next));
    setFitMode('none');
  }, [effectiveZoom]);

  const handleFitToWidth = useCallback(() => {
    setFitMode((m) => (m === 'width' ? 'none' : 'width'));
  }, []);

  const handleFitToPage = useCallback(() => {
    setFitMode((m) => (m === 'page' ? 'none' : 'page'));
  }, []);

  // ── Rotate handler ────────────────────────────────────────────────────────
  // Always apply optimistic rotation client-side for instant feedback. If
  // `onRotatePage` is wired the parent persists it on the server and the
  // affected image will refetch — we clear the optimistic entry once the new
  // blob arrives (see `handleBlobReady`).
  const handleRotate = useCallback(
    (delta: number) => {
      setPageRotations((prev) => {
        const next = new Map(prev);
        const current = next.get(visiblePage) ?? 0;
        next.set(visiblePage, (((current + delta) % 360) + 360) % 360);
        return next;
      });
      onRotatePage?.(visiblePage, delta);
    },
    [onRotatePage, visiblePage]
  );

  // ── Print handler ──
  const handlePrint = useCallback(async () => {
    setPrintMode(true);

    // Wait two frames for React to commit and the portal to mount
    await new Promise<void>((r) => requestAnimationFrame(() => r()));
    await new Promise<void>((r) => requestAnimationFrame(() => r()));

    // Wait for every print image to load + decode — otherwise the print
    // dialog snapshots the page before the blob-backed <img> tags render.
    const portal = document.getElementById('__print-portal');
    if (portal) {
      const imgs = Array.from(portal.querySelectorAll('img'));
      await Promise.all(
        imgs.map(async (img) => {
          if (!img.complete) {
            await new Promise<void>((resolve) => {
              img.addEventListener('load', () => resolve(), { once: true });
              img.addEventListener('error', () => resolve(), { once: true });
            });
          }
          try {
            await img.decode();
          } catch {
            // decode() can reject for cancelled/broken images — print anyway
          }
        })
      );
    }

    window.print();
    setPrintMode(false);
  }, []);

  // ── Download handler ──
  const handleDownload = useCallback(() => {
    if (!pdfUrl) return;
    const a = document.createElement('a');
    a.href = pdfUrl;
    a.download = 'document.pdf';
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success(t('downloadStarted'));
  }, [pdfUrl, t]);

  // ── Image natural dimensions callback ──
  // Also clears any optimistic CSS rotation for this page — once the new
  // server-rotated bytes have loaded, the baked-in rotation is enough and we
  // don't want to double-rotate via transform.
  const handleImageLoad = useCallback(
    (pageIndex: number, naturalWidth: number, naturalHeight: number) => {
      setImageNaturalDims((prev) => {
        const existing = prev.get(pageIndex);
        if (
          existing &&
          existing.width === naturalWidth &&
          existing.height === naturalHeight
        ) {
          return prev;
        }
        const next = new Map(prev);
        next.set(pageIndex, { width: naturalWidth, height: naturalHeight });
        return next;
      });
      setPageRotations((prev) => {
        if (!prev.has(pageIndex)) return prev;
        const next = new Map(prev);
        next.delete(pageIndex);
        return next;
      });
    },
    []
  );

  // ── Keyboard shortcuts ──
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;

      if (mod && e.key === 'f') {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
      if (mod && e.key === 'p') {
        e.preventDefault();
        handlePrint();
      }
      if (mod && (e.key === '=' || e.key === '+')) {
        e.preventDefault();
        handleZoomIn();
      }
      if (mod && e.key === '-') {
        e.preventDefault();
        handleZoomOut();
      }
      if (mod && e.key === '\\') {
        e.preventDefault();
        handleFitToWidth();
      }
      if (mod && e.key === '0') {
        e.preventDefault();
        handleFitToPage();
      }
      if (e.key === 'Escape' && searchOpen) {
        handleSearchClose();
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [
    searchOpen,
    handlePrint,
    handleZoomIn,
    handleZoomOut,
    handleFitToWidth,
    handleFitToPage,
    handleSearchClose
  ]);

  // ── PDF-only fallback (no page images) ──
  if (pages.length === 0 && !pdfUrl) {
    return (
      <div className='flex h-full flex-col items-center justify-center gap-3'>
        <FileText className='text-muted-foreground/20 h-16 w-16' />
      </div>
    );
  }

  if (pages.length === 0 && pdfUrl) {
    return (
      <iframe src={pdfUrl} className='h-full w-full' title='Document PDF' />
    );
  }

  return (
    <div className='relative h-full'>
      {/* Scrollable page area — overflow-auto so pages wider than the viewport
          at high zoom get a horizontal scrollbar, just like an embedded PDF. */}
      <div
        ref={scrollRef}
        className='h-full overflow-auto pb-14'
        onScroll={handleScroll}
      >
        <div className='flex flex-col items-center gap-6 p-4'>
          {pages.map((page, index) => {
            // Each page is sized in real pixels (naturalWidth × zoom). The
            // wrapper reserves the rotation-aware bounding box so a rotated
            // page stays inside its layout slot instead of bleeding into its
            // neighbours. Until the image loads we fall back to the container
            // width and an A4-ish aspect ratio so the layout doesn't jump.
            const naturalDims = imageNaturalDims.get(index);
            const rotation = pageRotations.get(index) ?? 0;
            const isSideways = rotation % 180 !== 0;
            const fallbackWidth = Math.max(containerSize.width - 32, 320);
            const baseWidth = naturalDims?.width ?? fallbackWidth;
            const baseHeight =
              naturalDims?.height ?? Math.round(fallbackWidth * 1.4142);
            const innerW = baseWidth * effectiveZoom;
            const innerH = baseHeight * effectiveZoom;
            const outerW =
              (isSideways ? baseHeight : baseWidth) * effectiveZoom;
            const outerH =
              (isSideways ? baseWidth : baseHeight) * effectiveZoom;
            return (
              <div
                key={index}
                ref={(el) => {
                  if (el) pageRefs.current.set(index, el);
                  else pageRefs.current.delete(index);
                }}
                style={{ width: `${outerW}px` }}
                className='shrink-0 transition-[width] duration-150'
              >
                {/* Page label */}
                <div className='mb-2 flex items-center gap-2'>
                  <Badge variant='outline' className='text-xs font-medium'>
                    {page.label}
                  </Badge>
                  {page.isProcessing && (
                    <Loader2 className='text-primary h-3 w-3 animate-spin' />
                  )}
                </div>

                {/* Page image with overlays */}
                <PageImage
                  imageUrl={page.imageUrl}
                  authEndpoint={page.authEndpoint}
                  chunks={page.chunks}
                  highlightedChunkIds={highlightedChunkIds}
                  activeChunkIds={activeChunkIds}
                  searchMatchChunkIds={searchMatchChunkIds}
                  activeSearchChunkId={activeSearchChunkId}
                  isProcessing={page.isProcessing}
                  qrStatus={page.qrStatus}
                  qrBbox={page.qrBbox}
                  qrLabel={page.qrLabel}
                  onQrLocationDecoded={page.onQrLocationDecoded}
                  onChunkClick={onChunkClick}
                  onChunkHover={onChunkHover}
                  onImageLoad={(w, h) => handleImageLoad(index, w, h)}
                  rotation={rotation}
                  innerWidth={innerW}
                  innerHeight={innerH}
                  outerWidth={outerW}
                  outerHeight={outerH}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Search bar */}
      <AnimatePresence>
        {searchOpen && (
          <SearchBar
            matches={searchMatches}
            currentIndex={currentMatchIndex}
            onQueryChange={setSearchQuery}
            onNext={handleSearchNext}
            onPrev={handleSearchPrev}
            onClose={handleSearchClose}
          />
        )}
      </AnimatePresence>

      {/* Toolbar */}
      <ViewerToolbar
        currentPage={visiblePage}
        totalPages={pages.length}
        effectiveZoom={effectiveZoom}
        fitMode={fitMode}
        canDownload={!!pdfUrl}
        isRotating={isRotating}
        canRotate={Boolean(onRotatePage)}
        onPrevPage={() => goToPage(visiblePage - 1)}
        onNextPage={() => goToPage(visiblePage + 1)}
        onGoToPage={goToPage}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onFitToWidth={handleFitToWidth}
        onFitToPage={handleFitToPage}
        onRotateLeft={() => handleRotate(-90)}
        onRotateRight={() => handleRotate(90)}
        onToggleSearch={() => setSearchOpen((v) => !v)}
        onPrint={handlePrint}
        onDownload={handleDownload}
        onRearrangePages={onRearrangePages}
      />

      {/* Print portal */}
      {printMode && <PrintView pages={pages} />}
    </div>
  );
}
